import { Capacitor } from "@capacitor/core";
import { setupIonicReact } from "@ionic/react";
import { configureStore } from "@reduxjs/toolkit";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";

import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import rootReducer from "./store/reducer";
import { createLogger } from "./utils/logger";

import "./index.css";
import reportWebVitals from "./reportWebVitals";
import "./styles/crisp-chat-fixed.css";

// Ant Design styles - required for Select, Tag, Modal, etc. in chef dashboard
import "antd/dist/antd.min.css";

// Ionic styles - only load on native platforms to avoid CSS conflicts on web
if (Capacitor.isNativePlatform()) {
  import("./theme/ionic-styles.js");
  setupIonicReact({ mode: "ios", animated: true });
}

const logger = createLogger("Index");

function saveToLocalStorage(state) {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("state", serializedState);
  } catch (err) {
    logger.log(err);
  }
}

export function loadFromLocalStorage() {
  try {
    const serializedState = localStorage.getItem("state");
    if (serializedState == null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    logger.log(err);
    return undefined;
  }
}

const persistState = loadFromLocalStorage();

const store = configureStore({
  reducer: rootReducer,
  preloadedState: persistState
});
store.subscribe(() => saveToLocalStorage(store.getState()));

// Preload Google Maps (Places) so CapacitorSafeAddressInput can use google.maps.places.Autocomplete.
async function preloadGoogleMapsIfNeeded() {
  const apiKey = import.meta.env.VITE_API_KEY_MAP;
  if (!apiKey || typeof window === "undefined") return;
  if (window.google?.maps?.places) return; // already loaded
  try {
    const { Loader } = await import("@googlemaps/js-api-loader");
    const loader = new Loader({
      apiKey,
      libraries: ["places"]
    });
    await loader.load();
    logger.log("Google Maps (Places) preloaded from main entry");
  } catch (err) {
    logger.error("Google Maps preload failed:", err);
  }
}

const container = document.getElementById("root");
const root = createRoot(container);

// Preload Maps script before first paint so Places autocomplete uses same context as CRA.
preloadGoogleMapsIfNeeded().then(() => {
  root.render(
    <ErrorBoundary>
      <Provider store={store}>
        <App />
      </Provider>
    </ErrorBoundary>
  );
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();

reportWebVitals();
